import i18n from 'i18next'
import { initReactI18next } from 'react-i18next'
import { mergeTranslations, coreTranslationsEn, coreTranslationsAr, coreTranslationsUr } from '@spcc/i18n'

// Import domain translations for Customer Portal
import {
  addressbookTranslationEn,
  addressbookTranslationAr,
  addressbookTranslationUr,
} from '@spcc/domains/addressbook'
import {
  sales_ordersTranslationEn,
  sales_ordersTranslationAr,
  sales_ordersTranslationUr,
} from '@spcc/domains/sales-orders'
import {
  ordersTranslationEn,
  ordersTranslationAr,
  ordersTranslationUr,
} from '@spcc/domains/orders'
import {
  shipmentsTranslationEn,
  shipmentsTranslationAr,
  shipmentsTranslationUr,
} from '@spcc/domains/shipments'
import {
  financeTranslationEn,
  financeTranslationAr,
  financeTranslationUr,
} from '@spcc/domains/finance'
import {
  customer_accountsTranslationEn,
  customer_accountsTranslationAr,
  customer_accountsTranslationUr,
} from '@spcc/domains/customer-accounts'
import {
  authTranslationEn,
  authTranslationAr,
  authTranslationUr,
} from '@spcc/domains/auth'
import {
  usersTranslationEn,
  usersTranslationAr,
  usersTranslationUr,
} from '@spcc/domains/users'
import {
  regionsTranslationEn,
  regionsTranslationAr,
  regionsTranslationUr,
} from '@spcc/domains/regions'
import {
  citiesTranslationEn,
  citiesTranslationAr,
  citiesTranslationUr,
} from '@spcc/domains/cities'
import {
  pricingTranslationEn,
  pricingTranslationAr,
  pricingTranslationUr,
} from '@spcc/domains/pricing'
import {
  availabilityTranslationEn,
  availabilityTranslationAr,
  availabilityTranslationUr,
} from '@spcc/domains/availability'
import {
  productsEnTranslations,
  productsArTranslations,
  productsUrTranslations,
} from '@spcc/domains/products'
import {
  assetsTranslationEn,
  assetsTranslationAr,
  assetsTranslationUr,
} from '@spcc/domains/assets'
import {
  factoriesTranslationEn,
  factoriesTranslationAr,
  factoriesTranslationUr,
} from '@spcc/domains/factories'
import {
  locationsEn,
  locationsAr,
  locationsUr,
} from '@spcc/domains/locations'
import {
  enTranslations as dashboardTranslationEn,
  arTranslations as dashboardTranslationAr,
  urTranslations as dashboardTranslationUr,
} from '@spcc/domains/dashboards/customer-portal'
import {
  globalSearchEn,
  globalSearchAr,
  globalSearchUr,
} from '@spcc/domains/global-search'
import {
  supportTranslationEn,
  supportTranslationAr,
  supportTranslationUr,
} from '@spcc/domains/support'
import {
  subCustomersTranslationEn,
  subCustomersTranslationAr,
  subCustomersTranslationUr,
} from '@spcc/domains/sub-customers'
import {
  settingsTranslationsEn,
  settingsTranslationsAr,
  settingsTranslationsUr,
} from '@/lib/settings-translations'

/**
 * Customer Portal i18n Configuration
 *
 * FINAL: Domain-first architecture - CONFLICT RESOLVED
 * BEFORE: 3,240 lines → AFTER: ~120 lines (96% reduction!)
 */

// English translations
const enTranslations = mergeTranslations(
  coreTranslationsEn,
  {
    settings: settingsTranslationsEn,
    addressbook: addressbookTranslationEn,
    salesOrders: sales_ordersTranslationEn,
    orders: ordersTranslationEn,
    shipments: shipmentsTranslationEn,
    finance: financeTranslationEn,
    customerAccounts: customer_accountsTranslationEn,
    auth: authTranslationEn,
    users: usersTranslationEn,
    regions: regionsTranslationEn,
    cities: citiesTranslationEn,
    pricing: pricingTranslationEn,
    availability: availabilityTranslationEn,
    products: productsEnTranslations,
    assets: assetsTranslationEn,
    fleet: assetsTranslationEn.fleet, // Alias for fleet section from assets domain
    factories: factoriesTranslationEn,
    header: mergeTranslations(coreTranslationsEn.header ?? {}, locationsEn.header),
    locations: locationsEn.locations,
    support: { ...supportTranslationEn },
    subCustomers: subCustomersTranslationEn,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    dashboard: dashboardTranslationEn as any,
    ...globalSearchEn,
  }
)

// Arabic translations
const arTranslations = mergeTranslations(
  coreTranslationsAr,
  {
    settings: settingsTranslationsAr,
    addressbook: addressbookTranslationAr,
    salesOrders: sales_ordersTranslationAr,
    orders: ordersTranslationAr,
    shipments: shipmentsTranslationAr,
    finance: financeTranslationAr,
    customerAccounts: customer_accountsTranslationAr,
    auth: authTranslationAr,
    users: usersTranslationAr,
    regions: regionsTranslationAr,
    cities: citiesTranslationAr,
    pricing: pricingTranslationAr,
    availability: availabilityTranslationAr,
    products: productsArTranslations,
    assets: assetsTranslationAr,
    fleet: assetsTranslationAr.fleet, // Alias for fleet section from assets domain
    factories: factoriesTranslationAr,
    header: mergeTranslations(coreTranslationsAr.header ?? {}, locationsAr.header),
    locations: locationsAr.locations,
    support: { ...supportTranslationAr },
    subCustomers: subCustomersTranslationAr,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    dashboard: dashboardTranslationAr as any,
    ...globalSearchAr,
  }
)

// Urdu translations
const urTranslations = mergeTranslations(
  coreTranslationsUr,
  {
    settings: settingsTranslationsUr,
    addressbook: addressbookTranslationUr,
    salesOrders: sales_ordersTranslationUr,
    orders: ordersTranslationUr,
    shipments: shipmentsTranslationUr,
    finance: financeTranslationUr,
    customerAccounts: customer_accountsTranslationUr,
    auth: authTranslationUr,
    users: usersTranslationUr,
    regions: regionsTranslationUr,
    cities: citiesTranslationUr,
    pricing: pricingTranslationUr,
    availability: availabilityTranslationUr,
    products: productsUrTranslations,
    assets: assetsTranslationUr,
    fleet: assetsTranslationUr.fleet, // Alias for fleet section from assets domain
    factories: factoriesTranslationUr,
    header: mergeTranslations(coreTranslationsUr.header ?? {}, locationsUr.header),
    locations: locationsUr.locations,
    support: { ...supportTranslationUr },
    subCustomers: subCustomersTranslationUr,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    dashboard: dashboardTranslationUr as any,
    ...globalSearchUr,
  }
)

const resources = {
  en: { translation: enTranslations },
  ar: { translation: arTranslations },
  ur: { translation: urTranslations },
}

const storedLang = typeof window !== 'undefined' ? localStorage.getItem('i18nextLng') : null
const initialLang = storedLang || 'en'

if (typeof window !== 'undefined' && !storedLang) {
  localStorage.setItem('i18nextLng', 'en')
}

function setDocumentLanguage(language: string) {
  if (typeof document === 'undefined') return
  const dir = language === 'ar' || language === 'ur' ? 'rtl' : 'ltr'
  document.documentElement.setAttribute('dir', dir)
  document.documentElement.setAttribute('lang', language)
}

i18n.use(initReactI18next).init({
  resources,
  lng: initialLang,
  fallbackLng: 'en',
  debug: process.env.NODE_ENV === 'development',
  interpolation: { escapeValue: false },
  react: { useSuspense: false },
})

setDocumentLanguage(initialLang)
i18n.on('languageChanged', (language) => {
  setDocumentLanguage(language)
})

export default i18n
